import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from '../../../components';
import {
  addEth,
  addOrRemoveSelectedNFT,
  AnswerSwapStep,
  changeNFTAmount,
  clearSelectedNFTs,
  removeSelectedNFT,
  resetAppState,
  storeAnswerSwapStep,
} from '../../../ducks/app/actions';
import {
  selectActiveSwap,
  selectAnswerSwapStep,
  selectEnteredEthAmount,
  selectSelectedNFTs,
} from '../../../ducks/app/selectors';
import { useUserNFTs } from '../../../hooks/apollo/useUserNFTs';
import { PageHeader } from '../../../components/SwapKiwi/PageHeader';
import { SearchBar } from '../../../components/SearchBar';
import { useFilteredNFTs } from '../../../hooks/apollo/useFilteredNFTs';
import { AddEth } from '../../../containers';
import { INFT } from '../../../types/nft';
import { ButtonSizes, SKButton } from '../../../components/SwapKiwi/Button';
import { SKCard } from '../../../components/SwapKiwi/Card';
import { Routes } from '../../../constants/routes';

import { ReactComponent as XClose } from '../../../assets/svg/icons/x-close-bold.svg';
import { ReactComponent as Arrow } from '../../../assets/svg/icons/arrow-right-no-color.svg';
import { ReactComponent as StatusIndicator } from '../../../assets/svg/icons/status-indicator.svg';
import { BadgeSizes, SKBadge } from '../../../components/SwapKiwi/Badge';
import { IconComp } from '../../../components/SwapKiwi/IconComponent';
import { NoDataContainer } from '../../../components/SwapKiwi/NoDataContainer';
import { ReactComponent as LeftArrow } from '../../../assets/svg/icons/arrow-left.svg';

export const CreateSwap: React.FC = () => {
  const history = useHistory();
  const selectedNFTs = useSelector(selectSelectedNFTs);
  const activeSwap = useSelector(selectActiveSwap);
  const answerSwapStep = useSelector(selectAnswerSwapStep);
  const dispatch = useDispatch();
  const { loading, data, customAPIloading } = useUserNFTs();
  const { setSearchText, filteredList } = useFilteredNFTs(data);
  const enteredEthAmount = useSelector(selectEnteredEthAmount);
  const [selected1155NFTs, setSelected1155NFTs] = useState<INFT[]>(
    selectedNFTs.filter((NFT) => parseInt(NFT.amount) > 0),
  );
  const [selected1155s, setSelected1155s] = useState<number>(
    selected1155NFTs.reduce((previousValue, currentValue) => {
      const current = currentValue.amount && parseInt(currentValue.amount) > 0 ? parseInt(currentValue.amount) : 1;
      return previousValue + current;
    }, 0),
  );
  const [selected721s, setSelected721s] = useState<number>(selectedNFTs.length - selected1155NFTs.length);
  const clearAll = useRef<boolean>(false);
  // const swapInProgress = localStorage.getItem('swapInProgress');
  useEffect(() => {
    if (clearAll.current) {
      setSelected1155NFTs([]);
      setSelected1155s(0);
      setSelected721s(0);
      clearAll.current = false;
    } else {
      const NFTs1155 = selectedNFTs.filter((NFT) => NFT.type === 'ERC1155');
      setSelected1155NFTs(NFTs1155);
      setSelected1155s(
        NFTs1155.reduce((previousValue, currentValue) => {
          const current = currentValue.amount && parseInt(currentValue.amount) > 0 ? parseInt(currentValue.amount) : 1;
          return previousValue + current;
        }, 0),
      );
      setSelected721s(selectedNFTs.length - NFTs1155.length);
    }
  }, [selectedNFTs, clearAll]);

  if (!loading && (!data || data?.length === 0)) {
    return (
      <NoDataContainer
        color="onion"
        message1="No NFTs found."
        hasCta={false}
        description="You need to add some NFTs to your wallet to be able to swap them."
      />
    );
  }

  if (loading) {
    return <Loader />;
  }

  const onAnswerSwapPickNext = (): void => {
    dispatch(storeAnswerSwapStep(AnswerSwapStep.SWAP_REVIEW));
    history.push(Routes.RESOLVE_SWAP.replace(':id', activeSwap?.id || ''));
  };

  const getSelectedSKCard = (NFT: INFT): JSX.Element => {
    const onNFTRemoved = (): void => {
      if (selectedNFTs.findIndex((selectedNFT) => selectedNFT.id === NFT.id) !== -1) {
        dispatch(removeSelectedNFT(NFT));
      }
    };

    return <SKCard size="sm" selectable={false} selected={false} nft={NFT} onRemove={onNFTRemoved} key={NFT.id} />;
  };

  const getSKCard = (NFT: INFT, isLast: boolean): JSX.Element => {
    const onNFTSelect = (): void => {
      dispatch(addOrRemoveSelectedNFT(NFT));
      if (selectedNFTs.findIndex((selectedNFT) => selectedNFT.id === NFT.id) === -1 && parseInt(NFT.amount) > 1)
        dispatch(changeNFTAmount({ id: NFT.id, amount: '1' }));
    };

    return (
      <SKCard
        size="lg"
        onSelect={onNFTSelect}
        selectable={true}
        selected={selectedNFTs.findIndex((selectedNFT) => selectedNFT.id === NFT.id) !== -1}
        key={NFT.id}
        nft={NFT}
        verified={NFT.verified}
        className={isLast ? 'lg:mr-auto' : ''}
      />
    );
  };

  return (
    <>
      <PageHeader
        background="onion"
        title="Creating Swap"
        description='<p class="text-lg text-cloud-900">Select the NFTs you want to swap. You can also add ETH to the swap.<br />Use the search bar to find certain NFTs. Once ready, hit “Next”.</p>'
        link={{ label: 'Learn More', url: '/faq' }}
        stickerWidth={272}
        notTopBorder={false}
      />
      <div className="bg-onion-100 py-3 border-b border-cloud-600">
        <div className="app-container mx-auto">
          <div className="flex items-center justify-between bg-white border border-cloud-900 rounded-xl py-2 px-1.75">
            <div className="flex items-center">
              <IconComp size={60} icon={StatusIndicator} />
              <div className="pl-2">
                <p className="text-sm font-bold">Step 1 of 2</p>
                <p className="text-md">Creating Swap</p>
                <p className="text-sm text-cloud-400">Select the NFTs you want to swap.</p>
              </div>
            </div>
            <SKButton
              color="tertiary"
              size={ButtonSizes.LG}
              iconLeft={LeftArrow}
              onClick={(): void => {
                dispatch(resetAppState());
                history.push(Routes.YOUR_NFTS);
              }}
              className="ml-auto mr-1.5"
            >
              Back
            </SKButton>
            <SKButton
              color="primary"
              size={ButtonSizes.XL}
              iconRight={Arrow}
              rightIconClasses={
                selectedNFTs.length === 0 ? 'stroke-current text-cloud-400' : 'stroke-current text-black'
              }
              onClick={
                answerSwapStep === AnswerSwapStep.PICK_NFT
                  ? onAnswerSwapPickNext
                  : (): void => {
                      history.push(Routes.CREATE_SWAP_CONFIRM);
                    }
              }
              disabled={selectedNFTs.length === 0}
            >
              Next
            </SKButton>
          </div>
        </div>
      </div>
      <div className="bg-onion-100 py-4 border-b border-cloud-600">
        <div className="app-container mx-auto">
          <div className="flex gap-1 mb-2">
            <SKBadge size={BadgeSizes.LG} color="outline">
              {enteredEthAmount ? enteredEthAmount : 0} ETH
            </SKBadge>
            {selected721s > 0 && (
              <SKBadge size={BadgeSizes.LG} color="outline">
                {selected721s} ERC721
              </SKBadge>
            )}
            {selected1155s > 0 && (
              <SKBadge size={BadgeSizes.LG} color="outline">
                {selected1155s} ERC1155
              </SKBadge>
            )}
            <button
              className="cursor-pointer text-md hover:font-semibold flex items-center gap-x-1"
              onClick={(): void => {
                dispatch(clearSelectedNFTs());
                dispatch(addEth(0));
                clearAll.current = true;
              }}
            >
              <span className="text-base text-cloud-900">Clear all</span>
              <IconComp icon={XClose} size={16} />
            </button>
          </div>
          <div className="selectedNFTs-wrapper flex flex-wrap gap-1">
            <AddEth />
            {selectedNFTs.map((NFT) => getSelectedSKCard(NFT))}
          </div>
        </div>
      </div>
      <div className="bg-onion-50 py-4">
        <div className="app-container mx-auto">
          <SearchBar onChange={setSearchText} className="border border-cloud-600 mb-4 rounded-xl" />

          <div className="flex flex-wrap justify-evenly gap-1.5">
            {filteredList.map((NFT, index) => getSKCard(NFT, index === filteredList.length - 1))}
            {customAPIloading && (
              <div className="NFT-tile center">
                <Loader />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
